/* SE2 · PLP header (sections/se2-plp-header.liquid), batch 2, 2026-11-03.
   One file for the collection pages' behaviour, loaded with defer from the section (never from a fragment Horizon
   morphs: morphed HTML does not run scripts). Seven parts:
     1. the SCENT | EMOTION toggle (fragment links without JS; here ?group= and aria-current)
     2. the filter: tiles and sheet share one state, matched against the section's card index
     3. the address: a single tile writes ?chip= (the live contract the home cards link to), anything wider
        ?family= ?mood= ?strength= ?size= ?stock=1
     4. the filter sheet (<dialog>): a draft that "Show N scents" applies and Close or Esc drops
     5. the motion pause: [data-se2-motion-paused] on the header AND on the grid container, which the product
        cards' notes read (.design/build-2026-11-03/b2-card-contract.md)
     6. the rooms of the diffuser page: a recommendation, never a filter
     7. Sort, as the live page sorts (pointer choices sort at once, keyboard choices wait for Apply, WCAG 3.2.2)
     8. On sale (2026-11-04, sale pricing): the "On sale" chip keeps the cards that show a sale now
        (product-card[data-se2-sale], not yet [data-se2-sale-over]: snippets/product-card.liquid). On Scents it is one
        more filter of part 2's state (?sale=1, the sheet's "On sale only" switch); on Diffusers, which have no other
        filter, it works alone. A card whose sale ends while the page is open leaves the filter, and the chip goes
        with the last one. */
(() => {
  if (window.se2PlpLoaded) return;
  window.se2PlpLoaded = true;

  const se2Fill = (tpl, vars) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in vars ? String(vars[k]) : m));
  const GROUPS = ['scent', 'emotion'];
  const GROUP_DIM = { scent: 'family', emotion: 'mood' };
  const DIMS = ['mood', 'family', 'strength', 'size'];
  const ITEMS = '.product-grid__item[data-product-id]';
  // The section's fold, the same table as its Liquid (mood hits) and the tile keys. Change both together.
  // 'fresh-clean' stays here for addresses (?mood=fresh-clean, ?chip=fresh-clean open Relax); a product's own moods
  // go through moodsOf() below, where it counts only as a last resort.
  const CANON = {
    woody: 'wood',
    spice: 'spicy',
    'de-stress': 'destress',
    cozy: 'comfort',
    energize: 'energy',
    uplifting: 'energy',
    'fresh-clean': 'relax',
  };
  const canon = (key) => CANON[key] || key;
  const words = (text) => String(text || '').split(/\s+/).filter(Boolean);
  // 2026-11-04: fresh-clean reads as Relax only when none of the product's other moods reaches an Emotion tile.
  // Beside Uplifting or Energize (Ocean Escape, California Sunset) it put them in Relax as well as Energy; alone
  // (November Nights) it is the product's one way into the tiles. The section's Liquid applies the same rule to
  // the tile counts; change both together.
  const SOFT_MOODS = { 'fresh-clean': 'relax' };
  const moodsOf = (text, tiles) => {
    const raw = words(text);
    const firm = raw.filter((key) => !(key in SOFT_MOODS)).map(canon);
    const out = new Set(firm);
    if (!firm.some((key) => tiles.has(key))) {
      for (const key of raw) if (key in SOFT_MOODS) out.add(SOFT_MOODS[key]);
    }
    return Array.from(out);
  };
  const splitKey = (value) => {
    const at = String(value || '').indexOf(':');
    return at < 0 ? ['', ''] : [value.slice(0, at), value.slice(at + 1)];
  };
  // 8. A card that shows a sale now: its tag is up and its inline guard has not marked the sale over.
  const onSale = (item) => Boolean(item.querySelector('product-card[data-se2-sale]:not([data-se2-sale-over])'));
  const saleCount = () => Array.from(document.querySelectorAll(ITEMS)).filter(onSale).length;
  const gridRoot = () => document.querySelector('results-list') || document.querySelector('.product-grid-container');
  const listRoot = () => document.querySelector('results-list') || document.querySelector('.product-grid');
  const groupOf = (root) => root.dataset.se2Group || root.dataset.se2StartGroup || 'scent';

  // Back and Forward restore the address first; nothing may write it while they do (part 2's group watcher).
  let popping = false;
  window.addEventListener(
    'popstate',
    () => {
      popping = true;
      setTimeout(() => {
        popping = false;
      }, 0);
    },
    true
  );

  /* ---- 1. the toggle ---- */
  const applyGroup = (root, group, announce) => {
    root.dataset.se2Group = group;
    for (const link of root.querySelectorAll('[data-se2-group-link]')) {
      if (link.dataset.se2GroupLink === group) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
    if (!announce) return;
    const live = root.querySelector('[data-se2-group-status]');
    if (live) live.textContent = se2Fill(root.dataset.se2MsgGroup || 'Showing [group] filters', { group });
  };

  const initGroups = (root) => {
    if (root.se2PlpGroupReady) return;
    root.se2PlpGroupReady = true;
    const live = document.createElement('span');
    live.className = 'visually-hidden';
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('data-se2-group-status', '');
    root.appendChild(live);

    const url = new URL(window.location.href);
    let targeted = null;
    try {
      targeted = window.location.hash ? root.querySelector(window.location.hash) : null;
    } catch {
      targeted = null;
    }
    const fromHash = targeted ? targeted.dataset.se2Target : '';
    const queried = url.searchParams.get('group') || '';
    const fromQuery = GROUPS.includes(queried) ? queried : '';
    applyGroup(root, fromHash || fromQuery || root.dataset.se2StartGroup || 'scent', false);
    // `:target` outranks the data attribute, so the fragment goes as soon as the script owns the group.
    if (fromHash) history.replaceState(null, '', `${url.pathname}${url.search}`);

    root.addEventListener('click', (event) => {
      const link = event.target.closest('[data-se2-group-link]');
      if (!link || !root.contains(link)) return;
      event.preventDefault();
      const group = link.dataset.se2GroupLink;
      if (group === root.dataset.se2Group) return;
      applyGroup(root, group, true);
      const next = new URL(window.location.href);
      next.searchParams.set('group', group);
      next.searchParams.delete('chip');
      next.hash = '';
      history.pushState({ se2Group: group }, '', next);
    });
  };

  window.addEventListener('popstate', () => {
    for (const root of document.querySelectorAll('[data-se2-plp-header]')) {
      if (!root.se2PlpGroupReady) continue;
      const group = new URL(window.location.href).searchParams.get('group') || '';
      applyGroup(root, GROUPS.includes(group) ? group : root.dataset.se2StartGroup || 'scent', false);
    }
  });

  /* ---- 2 to 4. the filter, its address and the sheet ---- */
  const initFilter = (root) => {
    const rows = root.querySelectorAll('[data-se2-derived]');
    if (rows.length === 0 || root.se2PlpFilterReady) return;
    root.se2PlpFilterReady = true;

    let index = {};
    try {
      index = JSON.parse(root.dataset.se2CardIndex || '{}');
    } catch {
      index = {};
    }
    for (const row of rows) {
      row.hidden = false;
      row.removeAttribute('data-se2-pending');
    }
    root.dataset.se2DerivedReady = '';

    const sheet = root.querySelector('[data-se2-sheet]');
    const trigger = root.querySelector('[data-se2-sheet-open]');
    const line = root.querySelector('[data-se2-filter-line]');
    const lineLabel = line ? line.querySelector('[data-se2-filter-line-label]') : null;
    const lineCount = line ? line.querySelector('[data-se2-filter-line-count]') : null;
    const statusRegion = root.querySelector('[data-se2-filter-one]');
    const labelOf = (el) => {
      const named = el.querySelector('[data-se2-label]');
      return (named ? named.textContent : el.textContent).trim();
    };

    // Every value this page can filter by, with the words its control shows.
    const known = { family: new Map(), mood: new Map(), strength: new Map(), size: new Map() };
    const tileKeys = { scent: new Set(), emotion: new Set() };
    for (const el of root.querySelectorAll('[data-se2-filter]')) {
      const [group, key] = splitKey(el.dataset.se2Filter);
      if (!GROUP_DIM[group]) continue;
      tileKeys[group].add(key);
      known[GROUP_DIM[group]].set(key, labelOf(el));
    }
    let stockEl = null;
    let saleEl = null;
    const saleChip = root.querySelector('[data-se2-sale-chip]');
    const saleLabel = root.dataset.se2SaleLabel || (saleChip ? saleChip.textContent.trim() : '');
    if (sheet) {
      for (const el of sheet.querySelectorAll('[data-se2-opt]')) {
        const [dim, key] = splitKey(el.dataset.se2Opt);
        if (dim === 'stock') {
          stockEl = el;
          continue;
        }
        if (dim === 'sale') {
          saleEl = el;
          continue;
        }
        if (known[dim] && !el.disabled) known[dim].set(key, labelOf(el));
      }
    }
    const stockLabel = (() => {
      const id = stockEl ? stockEl.getAttribute('aria-labelledby') : '';
      const holder = id ? document.getElementById(id) : null;
      return holder ? holder.textContent.trim() : '';
    })();

    const blank = () => ({ family: new Set(), mood: new Set(), strength: new Set(), size: new Set(), stock: false, sale: false });
    const copy = (s) => ({
      family: new Set(s.family),
      mood: new Set(s.mood),
      strength: new Set(s.strength),
      size: new Set(s.size),
      stock: s.stock,
      sale: s.sale,
    });
    const total = (s) =>
      s.family.size + s.mood.size + s.strength.size + s.size.size + (s.stock ? 1 : 0) + (s.sale ? 1 : 0);
    // The chip and the sheet's switch exist only while some card on the page shows a sale.
    const saleAvailable = () => Boolean(saleChip || saleEl) && saleCount() > 0;
    let state = blank();
    // The page opens with no filter on and the status region empty: 0 is what it already says.
    let spokenTotal = 0;

    // A card the walk never reached (page 2) still prints its own family handles, so a family still matches.
    const recordOf = (item) =>
      index[item.dataset.productId] || {
        s: Array.from(item.querySelectorAll('[data-se2-scent]'), (n) => n.dataset.se2Scent).join(' '),
        m: '',
        t: 0,
        z: '',
        a: 1,
      };
    const anyOf = (set, list) => set.size === 0 || list.some((key) => set.has(key));
    // OR inside a group, AND across groups.
    const matches = (item, s) => {
      const r = recordOf(item);
      if (!anyOf(s.family, words(r.s).map(canon))) return false;
      if (!anyOf(s.mood, moodsOf(r.m, tileKeys.emotion))) return false;
      if (!anyOf(s.strength, [String(r.t || 0)])) return false;
      if (!anyOf(s.size, words(r.z))) return false;
      if (s.stock && !Number(r.a)) return false;
      if (s.sale && !onSale(item)) return false;
      return true;
    };
    const countFor = (s) => Array.from(document.querySelectorAll(ITEMS)).filter((item) => matches(item, s)).length;
    const countText = (n) =>
      se2Fill(n === 1 ? root.dataset.se2CountOne || '[count] product' : root.dataset.se2CountOther || '[count] products', {
        count: n,
      });
    const labelsOf = (s) => {
      const out = [];
      for (const dim of DIMS) for (const key of s[dim]) out.push(known[dim].get(key) || key);
      if (s.stock && stockLabel) out.push(stockLabel);
      if (s.sale && saleLabel) out.push(saleLabel);
      return out;
    };

    const live = document.createElement('span');
    live.className = 'visually-hidden';
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('data-se2-filter-status', '');
    root.appendChild(live);

    // The note a filter shows when nothing on the page matches it. It sits after the results list, outside the
    // markup Horizon re-renders, and is put back whenever it has to show.
    let empty = null;
    const showEmpty = (on) => {
      const list = listRoot();
      if (!list) return;
      if (!on && (!empty || !empty.isConnected)) return;
      if (!empty) {
        empty = document.createElement('p');
        empty.className = 'se2-plph__empty';
        empty.setAttribute('data-se2-filter-empty', '');
        empty.hidden = true;
        const text = document.createElement('span');
        text.textContent = root.dataset.se2EmptyText || 'Nothing here yet.';
        const clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'se2-plph__empty-clear';
        clear.textContent = root.dataset.se2EmptyAction || 'Show all';
        clear.addEventListener('click', () => {
          const was = root.querySelector('[data-se2-filter][aria-pressed="true"]');
          reset();
          (was || trigger || root.querySelector('[data-se2-filter]'))?.focus({ preventScroll: true });
        });
        empty.append(text, ' ', clear);
      }
      if (!empty.isConnected) list.insertAdjacentElement('afterend', empty);
      empty.hidden = !on;
    };

    const apply = (announce) => {
      const items = document.querySelectorAll(ITEMS);
      const on = total(state);
      let shown = 0;
      for (const item of items) {
        const match = on === 0 || matches(item, state);
        item.hidden = !match;
        if (match) shown += 1;
      }
      showEmpty(on > 0 && items.length > 0 && shown === 0);
      // The grid's "Not sure yet?" card is no product: it steps aside while a filter narrows the grid.
      for (const tile of document.querySelectorAll('[data-se2-promo]')) tile.hidden = on > 0;

      for (const tile of root.querySelectorAll('[data-se2-filter]')) {
        const [group, key] = splitKey(tile.dataset.se2Filter);
        const pressed = Boolean(GROUP_DIM[group]) && state[GROUP_DIM[group]].has(key);
        tile.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      }
      for (const tile of root.querySelectorAll('[data-se2-filter-clear]')) {
        const dim = GROUP_DIM[tile.dataset.se2FilterClear];
        tile.setAttribute('aria-pressed', dim && state[dim].size === 0 ? 'true' : 'false');
      }
      for (const row of rows) {
        row.toggleAttribute('data-se2-picked', Boolean(row.querySelector('[data-se2-filter][aria-pressed="true"]')));
      }
      if (saleChip) saleChip.setAttribute('aria-pressed', state.sale ? 'true' : 'false');

      if (line) {
        line.hidden = !(on > 0 && items.length > 0);
        if (!line.hidden) {
          lineLabel.textContent = labelsOf(state).join(', ');
          lineCount.textContent = se2Fill(root.dataset.se2MsgOf || '[shown] of [total]', { shown, total: items.length });
        }
      }
      // 2026-11-04 (QA B2): the bar's count follows the filter, "5 scents" beside five cards, with the template's own
      // singular and plural; it said the collection total ("16 scents") whatever the grid showed. The server's text
      // is kept on the root and put back once the filter clears. Not a live region: the status below speaks.
      const counts = root.querySelectorAll('[data-se2-count]');
      if (counts.length > 0) {
        if (root.se2CountTotal === undefined) root.se2CountTotal = counts[0].textContent;
        const filtered = on > 0 && items.length > 0;
        root.se2CountFiltered = filtered;
        const text = filtered ? countText(shown) : root.se2CountTotal;
        for (const el of counts) if (el.textContent !== text) el.textContent = text;
      }
      if (trigger) {
        let badge = trigger.querySelector('.se2-plph__badge');
        if (on > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'se2-plph__badge';
            badge.setAttribute('aria-hidden', 'true');
            trigger.append(badge);
          }
          badge.textContent = String(on);
        } else if (badge) {
          badge.remove();
        }
      }
      // A role=status region speaks whenever its text is written, so it is written only when the number of
      // filters changes (2026-11-04). Every infinite-scroll append and Horizon morph runs apply(false), and each
      // used to fill the empty region with "0 filters applied".
      if (statusRegion && trigger && on !== spokenTotal) {
        spokenTotal = on;
        const text =
          on === 1
            ? statusRegion.dataset.se2FilterOne
            : (statusRegion.dataset.se2FilterOther || '').replace('[count]', String(on));
        if (text && statusRegion.textContent.trim() !== text) statusRegion.textContent = text;
      }

      if (!announce || items.length === 0) return;
      const one = items.length === 1;
      const vars = { shown, total: items.length };
      let said = one
        ? se2Fill(root.dataset.se2MsgProductOfOne || 'Showing [shown] of 1 product', vars)
        : se2Fill(root.dataset.se2MsgProductOfOther || 'Showing [shown] of [total] products', vars);
      if (on === 0)
        said = one
          ? root.dataset.se2MsgProductOne || 'Showing 1 product'
          : se2Fill(root.dataset.se2MsgProductAll || 'Showing all [total] products', vars);
      live.textContent = said;
    };

    // ---- 3. the address ----
    const PARAMS = ['chip', 'family', 'mood', 'strength', 'size', 'stock', 'sale'];
    const writeUrl = () => {
      const url = new URL(window.location.href);
      for (const p of PARAMS) url.searchParams.delete(p);
      const group = groupOf(root);
      const dim = GROUP_DIM[group];
      const single = total(state) === 1 && dim && state[dim].size === 1 && tileKeys[group].has([...state[dim]][0]);
      if (single) {
        url.searchParams.set('chip', [...state[dim]][0]);
      } else {
        for (const d of DIMS) if (state[d].size) url.searchParams.set(d, [...state[d]].join(','));
        if (state.stock) url.searchParams.set('stock', '1');
        if (state.sale) url.searchParams.set('sale', '1');
      }
      if (url.href !== window.location.href) history.replaceState(history.state, '', url);
    };
    const readUrl = () => {
      const url = new URL(window.location.href);
      const s = blank();
      const group = groupOf(root);
      const chip = canon((url.searchParams.get('chip') || '').trim().toLowerCase());
      if (chip && GROUP_DIM[group] && tileKeys[group].has(chip)) s[GROUP_DIM[group]].add(chip);
      for (const d of DIMS) {
        for (const raw of (url.searchParams.get(d) || '').split(',')) {
          let key = raw.trim().toLowerCase();
          if (d === 'family' || d === 'mood') key = canon(key);
          if (key && known[d].has(key)) s[d].add(key);
        }
      }
      if (url.searchParams.get('stock') === '1' && stockEl) s.stock = true;
      if (url.searchParams.get('sale') === '1' && saleAvailable()) s.sale = true;
      return s;
    };

    const reset = () => {
      state = blank();
      apply(true);
      writeUrl();
    };

    line?.querySelector('[data-se2-filter-line-clear]')?.addEventListener('click', () => {
      reset();
      (trigger || root.querySelector('[data-se2-filter]'))?.focus({ preventScroll: true });
    });

    // "On sale" is a switch of its own, ANDed with whatever else is chosen.
    if (saleChip) {
      saleChip.hidden = !saleAvailable();
      saleChip.addEventListener('click', () => {
        state.sale = !state.sale;
        apply(true);
        writeUrl();
      });
    }
    // A sale that ends while the page is open (each card's guard marks it) leaves the filter; the chip and the
    // switch go with the last one.
    const syncSale = () => {
      const any = saleAvailable();
      if (saleChip) saleChip.hidden = !any;
      if (saleEl) saleEl.closest('.se2-sheet__sale')?.toggleAttribute('hidden', !any);
      if (!any && state.sale) {
        state.sale = false;
        writeUrl();
      }
      apply(false);
    };
    const saleList = listRoot();
    if (saleList && (saleChip || saleEl)) {
      new MutationObserver(syncSale).observe(saleList, { subtree: true, attributes: true, attributeFilter: ['data-se2-sale-over'] });
    }

    // A tile is a quick single choice: an unpressed tile replaces its group's choice, a pressed one clears itself.
    root.addEventListener('click', (event) => {
      const tile = event.target.closest('[data-se2-filter], [data-se2-filter-clear]');
      if (!tile || !root.contains(tile) || (sheet && sheet.contains(tile))) return;
      if (tile.dataset.se2FilterClear) {
        const dim = GROUP_DIM[tile.dataset.se2FilterClear];
        if (dim) state[dim].clear();
      } else {
        const [group, key] = splitKey(tile.dataset.se2Filter);
        const dim = GROUP_DIM[group];
        if (!dim) return;
        if (state[dim].has(key)) state[dim].delete(key);
        else {
          state[dim].clear();
          state[dim].add(key);
        }
      }
      apply(true);
      writeUrl();
    });

    // Switching group drops the choice of the group being left (the live behaviour, and the boards'), except while
    // Back or Forward restore an address: the popstate handler below reads that one whole.
    let lastGroup = groupOf(root);
    new MutationObserver(() => {
      const group = groupOf(root);
      if (group === lastGroup) return;
      const left = GROUP_DIM[lastGroup];
      lastGroup = group;
      if (popping || !left || state[left].size === 0) return;
      state[left].clear();
      apply(true);
      writeUrl();
    }).observe(root, { attributes: true, attributeFilter: ['data-se2-group'] });

    // Infinite scroll appends cards and Horizon morphs the list: the choice is re-applied to what is there now.
    const list = listRoot();
    if (list) new MutationObserver(() => apply(false)).observe(list, { childList: true, subtree: true });

    window.addEventListener('popstate', () => {
      if (!root.isConnected) return;
      lastGroup = groupOf(root);
      state = readUrl();
      apply(true);
    });

    // ---- 4. the sheet ----
    if (sheet && trigger && typeof sheet.showModal === 'function') {
      const showBtn = sheet.querySelector('[data-se2-sheet-apply]');
      const clearBtn = sheet.querySelector('[data-se2-sheet-clear]');
      let draft = null;
      let breathTimer = 0;

      const paintSheet = () => {
        if (!draft) return;
        for (const el of sheet.querySelectorAll('[data-se2-opt]')) {
          const [dim, key] = splitKey(el.dataset.se2Opt);
          const on = dim === 'stock' ? draft.stock : dim === 'sale' ? draft.sale : Boolean(draft[dim] && draft[dim].has(key));
          el.setAttribute(el.getAttribute('role') === 'switch' ? 'aria-checked' : 'aria-pressed', on ? 'true' : 'false');
        }
        const n = countFor(draft);
        if (showBtn) {
          showBtn.textContent = se2Fill(showBtn.dataset.se2ShowLabel || 'Show [count]', { count: countText(n) });
          showBtn.disabled = n === 0;
        }
        if (clearBtn) clearBtn.disabled = total(draft) === 0;
      };

      const open = () => {
        draft = copy(state);
        paintSheet();
        sheet.showModal();
        document.documentElement.classList.add('se2-sheet-open');
        // One breath as the sheet opens (FilterProposed): under 5 s, so it needs no pause of its own; none while
        // the page's motion is paused, none without html.se2-motion (reduced motion).
        clearTimeout(breathTimer);
        sheet.classList.remove('is-breathing');
        if (document.documentElement.classList.contains('se2-motion') && !root.hasAttribute('data-se2-motion-paused')) {
          void sheet.offsetWidth;
          sheet.classList.add('is-breathing');
          breathTimer = setTimeout(() => sheet.classList.remove('is-breathing'), 4600);
        }
        requestAnimationFrame(() => sheet.classList.add('is-in'));
      };

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        open();
      });

      sheet.addEventListener('click', (event) => {
        // the backdrop: a click on the dialog box itself, outside the panel
        if (event.target === sheet) {
          sheet.close();
          return;
        }
        if (event.target.closest('[data-se2-sheet-close]')) {
          sheet.close();
          return;
        }
        if (event.target.closest('[data-se2-sheet-clear]')) {
          draft = blank();
          paintSheet();
          return;
        }
        if (event.target.closest('[data-se2-sheet-apply]')) {
          state = copy(draft);
          draft = null;
          sheet.close();
          apply(true);
          writeUrl();
          return;
        }
        const opt = event.target.closest('[data-se2-opt]');
        if (!opt || opt.disabled || !draft) return;
        const [dim, key] = splitKey(opt.dataset.se2Opt);
        if (dim === 'stock') draft.stock = !draft.stock;
        else if (dim === 'sale') draft.sale = !draft.sale;
        else if (draft[dim]) {
          if (draft[dim].has(key)) draft[dim].delete(key);
          else draft[dim].add(key);
        }
        paintSheet();
      });

      sheet.addEventListener('close', () => {
        draft = null;
        sheet.classList.remove('is-in', 'is-breathing');
        clearTimeout(breathTimer);
        document.documentElement.classList.remove('se2-sheet-open');
      });
    } else if (trigger) {
      // No <dialog> support: the pill has nothing to open, so it gives its place back.
      trigger.hidden = true;
    }

    // The address, pressed silently as the page's starting state; an unknown value is dropped.
    state = readUrl();
    if (total(state) > 0) {
      apply(false);
      const pressed = root.querySelector('[data-se2-filter][aria-pressed="true"]');
      const row = pressed ? pressed.closest('[data-se2-chips]') : null;
      if (row && row.scrollWidth > row.clientWidth) {
        const r = row.getBoundingClientRect();
        const c = pressed.getBoundingClientRect();
        row.scrollLeft += c.left + c.width / 2 - (r.left + r.width / 2);
      }
    }
    writeUrl();
  };

  /* ---- 5. the motion pause ---- */
  const STORE_KEY = 'se2-plph-motion-paused';
  const readChoice = () => {
    try {
      return window.sessionStorage.getItem(STORE_KEY);
    } catch {
      return null;
    }
  };
  const writeChoice = (value) => {
    try {
      window.sessionStorage.setItem(STORE_KEY, value);
    } catch {
      /* storage is blocked: the choice lasts as long as the page */
    }
  };

  const initMotion = (root) => {
    const toggle = root.querySelector('[data-se2-motion-toggle]');
    if (!toggle || root.se2PlpMotionReady) return;
    root.se2PlpMotionReady = true;
    let paused = readChoice() === '1';
    const mark = () => {
      root.toggleAttribute('data-se2-motion-paused', paused);
      const grid = gridRoot();
      if (grid) grid.toggleAttribute('data-se2-motion-paused', paused);
    };
    const paint = () => {
      mark();
      toggle.dataset.state = paused ? 'paused' : 'playing';
      toggle.setAttribute('aria-label', (paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause) || '');
    };
    paint();
    toggle.hidden = false;
    toggle.addEventListener('click', () => {
      paused = !paused;
      paint();
      writeChoice(paused ? '1' : '0');
    });
    // A morph of the results list rewrites its attributes from the server's HTML: the pause is put back.
    const grid = gridRoot();
    if (grid) {
      new MutationObserver(() => {
        if (grid.hasAttribute('data-se2-motion-paused') !== paused) mark();
      }).observe(grid, { attributes: true, attributeFilter: ['data-se2-motion-paused'] });
    }
  };

  /* ---- 6. the rooms ---- */
  const initRooms = (root) => {
    const rooms = root.querySelector('[data-se2-rooms]');
    if (!rooms || root.se2PlpRoomsReady) return;
    root.se2PlpRoomsReady = true;
    rooms.hidden = false;
    rooms.removeAttribute('data-se2-pending');
    const tiles = Array.from(rooms.querySelectorAll('[data-se2-room]'));
    const tileFor = (key) => tiles.find((tile) => tile.dataset.se2Room === key) || null;
    const live = document.createElement('span');
    live.className = 'visually-hidden';
    live.setAttribute('aria-live', 'polite');
    rooms.appendChild(live);
    let active = '';

    const paint = (announce) => {
      const tile = tileFor(active);
      const best = tile ? tile.dataset.se2BestId || '' : '';
      for (const t of tiles) t.setAttribute('aria-pressed', t === tile ? 'true' : 'false');
      // Every in-stock diffuser card prints its "Best for you" tag hidden; product-card[data-se2-best] shows it and
      // starts its halo (assets/se2-card.css, .design/build-2026-11-03/b2-card-contract.md).
      for (const item of document.querySelectorAll(ITEMS)) {
        const on = best !== '' && item.dataset.productId === best;
        for (const card of item.querySelectorAll('product-card')) card.toggleAttribute('data-se2-best', on);
      }
      const grid = gridRoot();
      if (grid) {
        if (active) grid.setAttribute('data-se2-room', active);
        else grid.removeAttribute('data-se2-room');
      }
      if (!announce) return;
      live.textContent = tile && tile.dataset.se2BestTitle ? se2Fill(rooms.dataset.se2BestStatus || '[name]', { name: tile.dataset.se2BestTitle }) : '';
    };
    const writeUrl = () => {
      const url = new URL(window.location.href);
      if (active) url.searchParams.set('room', active);
      else url.searchParams.delete('room');
      if (url.href !== window.location.href) history.replaceState(history.state, '', url);
    };
    const fromUrl = () => {
      const key = (new URL(window.location.href).searchParams.get('room') || '').trim().toLowerCase();
      return tileFor(key) ? key : '';
    };

    rooms.addEventListener('click', (event) => {
      const tile = event.target.closest('[data-se2-room]');
      if (!tile || !rooms.contains(tile)) return;
      active = tile.dataset.se2Room === active ? '' : tile.dataset.se2Room;
      paint(true);
      writeUrl();
    });
    const list = listRoot();
    if (list) new MutationObserver(() => active && paint(false)).observe(list, { childList: true, subtree: true });
    window.addEventListener('popstate', () => {
      if (!root.isConnected) return;
      active = fromUrl();
      paint(false);
    });
    active = fromUrl();
    if (active) paint(false);
  };

  /* ---- 8. On sale, on a page with no other filter (Diffusers) ---- */
  const initSaleOnly = (root) => {
    const chip = root.querySelector('[data-se2-sale-chip]');
    if (!chip || root.se2PlpFilterReady || root.se2PlpSaleReady) return;
    root.se2PlpSaleReady = true;
    const live = document.createElement('span');
    live.className = 'visually-hidden';
    live.setAttribute('aria-live', 'polite');
    root.appendChild(live);
    const counts = root.querySelectorAll('[data-se2-count]');
    const totalText = counts.length ? counts[0].textContent : '';
    let on = new URL(window.location.href).searchParams.get('sale') === '1';

    const apply = (announce) => {
      const items = Array.from(document.querySelectorAll(ITEMS));
      const any = items.some(onSale);
      chip.hidden = !any;
      if (!any) on = false;
      let shown = 0;
      for (const item of items) {
        const match = !on || onSale(item);
        item.hidden = !match;
        if (match) shown += 1;
      }
      for (const tile of document.querySelectorAll('[data-se2-promo]')) tile.hidden = on;
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      const text = on
        ? se2Fill(shown === 1 ? root.dataset.se2CountOne || '[count] product' : root.dataset.se2CountOther || '[count] products', { count: shown })
        : totalText;
      for (const el of counts) if (text && el.textContent !== text) el.textContent = text;
      if (!announce || items.length === 0) return;
      const vars = { shown, total: items.length };
      live.textContent = on
        ? se2Fill(root.dataset.se2MsgProductOfOther || 'Showing [shown] of [total] products', vars)
        : se2Fill(root.dataset.se2MsgProductAll || 'Showing all [total] products', vars);
    };
    const writeUrl = () => {
      const url = new URL(window.location.href);
      if (on) url.searchParams.set('sale', '1');
      else url.searchParams.delete('sale');
      if (url.href !== window.location.href) history.replaceState(history.state, '', url);
    };

    chip.addEventListener('click', () => {
      on = !on;
      apply(true);
      writeUrl();
    });
    const list = listRoot();
    if (list) {
      new MutationObserver(() => apply(false)).observe(list, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-se2-sale-over'],
      });
    }
    window.addEventListener('popstate', () => {
      if (!root.isConnected) return;
      on = new URL(window.location.href).searchParams.get('sale') === '1';
      apply(false);
    });
    apply(false);
    writeUrl();
  };

  /* ---- 7. Sort ---- */
  const sortTo = (value) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    url.hash = '';
    window.location.assign(url.toString());
  };
  const initSort = () => {
    for (const form of document.querySelectorAll('[data-se2-sort]')) {
      if (form.se2SortReady) continue;
      const select = form.querySelector('select[name="sort_by"]');
      if (!select) continue;
      form.se2SortReady = true;
      const go = form.querySelector('[data-se2-sort-go]');
      if (go) go.hidden = true;
      select.dataset.se2Applied = select.value;
      let keyed = false;
      select.addEventListener('pointerdown', () => {
        keyed = false;
      });
      select.addEventListener('keydown', (event) => {
        if (['Tab', 'Shift', 'Escape', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
        keyed = true;
      });
      select.addEventListener('change', () => {
        if (!keyed) {
          sortTo(select.value);
          return;
        }
        if (go) go.hidden = select.value === select.dataset.se2Applied;
      });
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        sortTo(select.value);
      });
    }
  };

  // Horizon's own drawer (collections without picture tiles) re-renders only the grid: the count, the select and
  // the pill's badge are brought up to date from its event, as before.
  document.addEventListener(
    'shopify:collection:update',
    (event) => {
      const sort = new URL(window.location.href).searchParams.get('sort_by');
      for (const select of document.querySelectorAll('[data-se2-sort] select[name="sort_by"]')) {
        const next = sort || select.dataset.se2Default || '';
        if (Array.from(select.options).some((option) => option.value === next)) select.value = next;
        select.dataset.se2Applied = select.value;
        const go = select.form && select.form.querySelector('[data-se2-sort-go]');
        if (go) go.hidden = true;
      }
      let active = 0;
      let price = false;
      for (const [key, value] of new URL(window.location.href).searchParams) {
        if (!key.startsWith('filter.') || value === '') continue;
        if (/\.price\.(gte|lte)$/.test(key)) price = true;
        else active += 1;
      }
      if (price) active += 1;
      for (const trigger of document.querySelectorAll('.se2-plph__trigger:not([data-se2-sheet-open])')) {
        let badge = trigger.querySelector('.se2-plph__badge');
        if (active > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'se2-plph__badge';
            badge.setAttribute('aria-hidden', 'true');
            trigger.append(badge);
          }
          badge.textContent = String(active);
        } else if (badge) {
          badge.remove();
        }
        const status = trigger.parentElement && trigger.parentElement.querySelector('[data-se2-filter-one]');
        if (status) {
          const text = active === 1 ? status.dataset.se2FilterOne : (status.dataset.se2FilterOther || '').replace('[count]', String(active));
          if (text && status.textContent.trim() !== text) status.textContent = text;
        }
      }
      const pending = event.promise || (event.detail && event.detail.promise);
      if (!pending || typeof pending.then !== 'function') return;
      pending
        .then((result) => {
          let count = result && typeof result.productsCount === 'number' ? result.productsCount : NaN;
          if (Number.isNaN(count)) {
            const holder = document.querySelector('[data-results-count]');
            count = holder ? Number(holder.getAttribute('data-results-count')) : NaN;
          }
          if (Number.isNaN(count)) return;
          for (const root of document.querySelectorAll('[data-se2-plp-header]')) {
            const text = se2Fill(count === 1 ? root.dataset.se2CountOne : root.dataset.se2CountOther, { count });
            if (!text) continue;
            // the new total is what a cleared tile filter puts back; while one is on, the bar keeps its count
            root.se2CountTotal = text;
            if (root.se2CountFiltered) continue;
            for (const el of root.querySelectorAll('[data-se2-count]')) el.textContent = text;
          }
        })
        .catch(() => {});
    },
    true
  );

  const init = () => {
    for (const root of document.querySelectorAll('[data-se2-plp-header]')) {
      initGroups(root);
      initFilter(root);
      initMotion(root);
      initRooms(root);
      initSaleOnly(root);
    }
    initSort();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
